import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

const DURATION = 10;

export function TapWar() {
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [p1, setP1] = useState(0);
  const [p2, setP2] = useState(0);
  const [time, setTime] = useState(DURATION);

  useEffect(() => {
    if (phase !== "running") return;
    if (time <= 0) { setPhase("done"); return; }
    const t = setTimeout(() => setTime((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, time]);

  const start = () => { setP1(0); setP2(0); setTime(DURATION); setPhase("running"); };

  useEffect(() => {
    const key = (e) => {
      if (phase !== "running") return;
      const k = e.key.toLowerCase();
      if (k === "q") { e.preventDefault(); setP1((x) => x + 1); }
      if (k === "p") { e.preventDefault(); setP2((x) => x + 1); }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [phase]);

  const winner = p1 === p2 ? "Tie!" : p1 > p2 ? "Player 1 wins! 🏆" : "Player 2 wins! 🏆";

  return (
    <div className="w-full max-w-2xl mx-auto" data-testid="tap-war">
      <div className="text-center mb-4 font-mono">
        {phase === "running" ? <span className={`text-2xl ${time <= 3 ? "text-neon-pink animate-blink" : "text-neon-yellow"}`}>⏱ {time}s</span>
          : phase === "done" ? <span className="font-display font-black text-2xl text-neon-lime uppercase" data-testid="tapwar-winner">{winner}</span>
          : <span className="text-zinc-400">Tap as fast as you can for {DURATION} seconds!</span>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <button onClick={() => phase === "running" && setP1((x) => x + 1)} data-testid="tapwar-p1"
          disabled={phase !== "running"} className="rounded-xl border-2 border-neon-cyan py-10 text-center active:scale-95 transition-transform"
          style={{ background: "rgba(0,240,255,0.08)" }}>
          <p className="font-display font-bold uppercase text-sm text-neon-cyan">Player 1 · Q</p>
          <p className="font-display font-black text-6xl text-neon-cyan">{p1}</p>
          <p className="text-zinc-500 text-xs">TAP!</p>
        </button>
        <button onClick={() => phase === "running" && setP2((x) => x + 1)} data-testid="tapwar-p2"
          disabled={phase !== "running"} className="rounded-xl border-2 border-neon-pink py-10 text-center active:scale-95 transition-transform"
          style={{ background: "rgba(255,0,60,0.08)" }}>
          <p className="font-display font-bold uppercase text-sm text-neon-pink">Player 2 · P</p>
          <p className="font-display font-black text-6xl text-neon-pink">{p2}</p>
          <p className="text-zinc-500 text-xs">TAP!</p>
        </button>
      </div>

      <div className="text-center mt-6">
        {phase !== "running" && (
          <Button onClick={start} data-testid="tapwar-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest">
            {phase === "done" ? <><RotateCcw className="w-4 h-4 mr-1" /> Rematch</> : "Start Tap War"}
          </Button>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-4 text-center">Two players, one keyboard. P1 = Q, P2 = P (or tap your panel).</p>
    </div>
  );
}
